// ----------      START   IMPORT  BLOCK        ----------
import React, { useEffect, useState } from 'react';
import { Form, Button, Row, Col, Accordion, Spinner, Alert } from 'react-bootstrap';
import { useLanguage } from "../../../../API/Context/LanguageContext";
import { useUserMeAddresses } from '../../../../API/Hooks/useUserMeAddresses';
import { VocabAddresses as vocab } from '../../../../ConfigFiles/multilingual/VocabAddresses';
import { VocabButtons as btn_vocab } from '../../../../ConfigFiles/multilingual/VocabButtons';
import SpecialtySubPicker, { type SelectedItem } from '../ProOnboarding/Steps/Widgets/SpecialtySubPicker';
import StateService from '../../../../API/Services/Geography/StateService';
import type { AddressRead } from '../../../../Types/schema-helpers';
// ----------      END   IMPORT  BLOCK        ----------

const statesService = new StateService();

// Clean empty template without bogus IDs
const emptyAddress: Partial<AddressRead> = {
  street: '',
  number: '',
  state: '' as any,
  state_name: '',
  zip_code: '',
  google_maps_url: null,
  verified: false,
  active: false,
  deleted: false,
  interior_number: null,
};

const AddressesForm: React.FC = () => {
  const { lan } = useLanguage();
  const { userAddresses, isDataLoading, dataError, saveAllAddresses } = useUserMeAddresses();

  const [localAddresses, setLocalAddresses] = useState<AddressRead[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (userAddresses && userAddresses.length > 0) {
      setLocalAddresses(userAddresses);
    } else {
      setLocalAddresses([{ ...emptyAddress } as AddressRead]);
    }
  }, [userAddresses]);

  const handleChange = <K extends keyof AddressRead>(
    index: number,
    field: K,
    value: AddressRead[K]
  ): void => {
    setLocalAddresses((prev) => {
      const target = prev[index];
      if (!target) return prev;

      const updated = [...prev];
      updated[index] = { ...target, [field]: value };
      return updated;
    });
  };

  const handleStateSelect = (index: number, item: SelectedItem | null): void => {
    setLocalAddresses((prev) => {
      const target = prev[index];
      if (!target) return prev;

      const updated = [...prev];
      updated[index] = {
        ...target,
        state: item ? (item.id as any) : "",
        state_name: item ? item.name : "",
      };
      return updated;
    });
  };

  const handleAdd = (): void => {
    setLocalAddresses((prev) => [...prev, { ...emptyAddress } as AddressRead]);
  };

  const handleRemove = (index: number): void => {
    setLocalAddresses((prev) => {
      const target = prev[index];
      if (!target) return prev;

      const updated = [...prev];
      if (target.id) {
        updated[index] = { ...target, deleted: true };
      } else {
        updated.splice(index, 1);
      }
      return updated;
    });
  };

  const handleSaveAll = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await saveAllAddresses(localAddresses);
    } catch (err) {
      console.error("Failed to save addresses:", err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isDataLoading && localAddresses.length === 0) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const activeAddresses = localAddresses.filter((a) => !a.deleted);

  return (
    <div className="address-widget-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>{vocab.addresses?.[lan] || "Addresses"}</h3>
        <Button 
          type="button"
          variant="success" 
          size="sm" 
          onClick={handleSaveAll}
          disabled={isSaving}
        >
          {isSaving ? <Spinner size="sm" animation="border" /> : (btn_vocab.save?.[lan] || "Save Changes")}
        </Button>
      </div>
      
      {dataError && <Alert variant="danger">{String(dataError)}</Alert>}

      <Accordion alwaysOpen defaultActiveKey="0">
        {localAddresses.map((address, index) =>
          address.deleted ? null : (
            <Accordion.Item eventKey={index.toString()} key={address.id || `new-${index}`}>
              <Accordion.Header>
                {address.street
                  ? `${address.street} ${address.number || ''}`
                  : `${vocab.address?.[lan] || "Address"} ${index + 1}`}
              </Accordion.Header>
              <Accordion.Body>
                {/* --- Street --- */}
                <Row className="mb-2">
                  <Col>
                    <Form.Group>
                      <Form.Label>{vocab.street?.[lan] || "Street"}</Form.Label>
                      <Form.Control
                        type="text"
                        value={address.street || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                          handleChange(index, 'street', e.target.value)
                        }
                        placeholder="e.g. Main St"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* --- Numbers & Zip --- */}
                <Row className="mb-2">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{vocab.number?.[lan] || "Exterior Number"}</Form.Label>
                      <Form.Control
                        type="text"
                        value={address.number || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                          handleChange(index, 'number', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{vocab.interior_number?.[lan] || "Interior Number"}</Form.Label>
                      <Form.Control
                        type="number"
                        value={address.interior_number ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                          handleChange(
                            index, 
                            'interior_number', 
                            e.target.value ? parseInt(e.target.value, 10) : null
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{vocab.zip_code?.[lan] || "Zip Code"}</Form.Label>
                      <Form.Control
                        type="text"
                        value={address.zip_code || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                          handleChange(index, 'zip_code', e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* --- State & Google Maps --- */}
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{vocab.state?.[lan] || "State"}</Form.Label>
                      <SpecialtySubPicker
                        searchFunction={(query: string, signal?: AbortSignal) => statesService.searchState(query, signal)}
                        selectedValue={address.state ? String(address.state) : ""}
                        selectedLabel={address.state_name || ""}
                        placeholder={vocab.state?.[lan] || "Select State"}
                        onSelect={(item: SelectedItem | null) => handleStateSelect(index, item)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{vocab.google_maps_url?.[lan] || "Google Maps URL"}</Form.Label>
                      <Form.Control
                        type="text"
                        value={address.google_maps_url || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                          handleChange(index, 'google_maps_url', e.target.value || null)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Button
                  type="button"
                  variant="outline-danger"
                  size="sm"
                  className="mt-2"
                  onClick={() => handleRemove(index)}
                  disabled={activeAddresses.length === 1 && !address.id}
                >
                  {btn_vocab.remove?.[lan] || "Remove"}
                </Button>
              </Accordion.Body>
            </Accordion.Item>
          )
        )}
      </Accordion>

      <div className="mt-3">
        <Button type="button" variant="outline-primary" onClick={handleAdd}>
          + {btn_vocab.add?.[lan] || "Add Another Address"}
        </Button>
      </div>
    </div>
  );
};

export default AddressesForm;